import type { CampaignRegime } from '../../campaign/campaign.types';
import { num } from './format';
import { PROMO_ICON_PATHS, promoBody, promoStats } from './promo';
import { barView, outboundRel, outboundUrl, tileOf } from './raise';
import type { LogoState } from './logo';
import {
  barOf,
  count,
  dayAfter,
  daysLeft,
  fullMoney,
  fundedPct,
  multiple,
  parts,
  pctText,
  regimeLabel,
  shortDay,
  stated,
  urgencyOf,
  weekdayDay,
} from './raise-figures';
import { BRAND_NAME } from '../../shared/brand';

/**
 * The live raises index, /raises (views/raises.hbs): the raises closing in
 * the next ten days, as a timeline of dots and a list of cards, which the
 * reader sorts. A hub (site/raise-hubs.ts) draws the same page with a scope:
 * every live raise on one platform, in one country, sector or regulation,
 * instead of the window; a raise page's "All Crowdcube raises" goes to one.
 *
 * The server draws the page whole, in London's zone, so it reads complete
 * without its script. The script (client/raises.ts) runs `indexState` again
 * in the reader's own zone and with their choices, and moves the cards and
 * dots the server drew to match; so the server draws a card for every raise
 * that could be in the window in any zone, and hides the ones that are not
 * in London's.
 */

/** A live raise as the index lists it. Dates are ISO strings, as the script reads them. */
export interface LiveRaise {
  id: string;
  /** Its public slug: /raises/{slug}. */
  slug: string;
  name: string;
  /** Its logo in our store (RaiseLogoService), never the platform's address. */
  logo: LogoState;
  platformId: string | null;
  platformName: string;
  regime: CampaignRegime;
  /** Where it is offered from: the platform's country, else the issuer's. */
  country: string | null;
  currency: string | null;
  raised: number | null;
  target: number | null;
  investors: number | null;
  closeAt: string | null;
  /** Its page on the platform; a Form C row's is the filing on SEC EDGAR. */
  url: string;
  /** A Form C row: its link is the filing, not a platform's page. */
  filing: boolean;
  /** Raise Sonar is paid for what it sends the platform (platform.affiliate). */
  affiliate: boolean;
}

/** How far ahead the timeline and the list look, in calendar days. */
export const WINDOW_DAYS = 10;

/**
 * How far ahead the server reads: a day more than the window, for a reader
 * whose today is still London's yesterday.
 */
export const READ_DAYS = WINDOW_DAYS + 1;

export type SortKey = 'closing' | 'funded' | 'investors';

export const SORTS: { key: SortKey; label: string }[] = [
  { key: 'closing', label: 'Closing soon' },
  { key: 'funded', label: 'Most funded' },
  { key: 'investors', label: 'Most investors' },
];

/** ?sort=: "funded" or "investors"; anything else is the default. */
export function sortFrom(value: unknown): SortKey {
  return value === 'funded' || value === 'investors' ? value : 'closing';
}

/**
 * The address's query with a sort: the rest kept as it is (a view's
 * filters, utm_*), the default leaving its parameter out.
 */
export function withSort(search: string, sort: SortKey): string {
  const query = new URLSearchParams(search);
  query.delete('sort');
  if (sort !== 'closing') query.set('sort', sort);
  const text = query.toString();
  return text ? `?${text}` : '';
}

const closeOf = (r: LiveRaise) => (r.closeAt ? new Date(r.closeAt) : null);

/** Closing within the window in `zone`, and not yet closed. */
export function inWindow(r: LiveRaise, now: Date, zone: string): boolean {
  const close = closeOf(r);
  return (
    close !== null && close > now && daysLeft(close, now, zone) <= WINDOW_DAYS
  );
}

/** Close first, then name, then id: a total order. */
function byClose(a: LiveRaise, b: LiveRaise): number {
  const at = (r: LiveRaise) =>
    r.closeAt ? Date.parse(r.closeAt) : Number.POSITIVE_INFINITY;
  return (
    at(a) - at(b) ||
    a.name.localeCompare(b.name) ||
    (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)
  );
}

/** Descending, a missing figure last, then by close. */
function byMost(
  figure: (r: LiveRaise) => number | null,
): (a: LiveRaise, b: LiveRaise) => number {
  return (a, b) => {
    const x = figure(a);
    const y = figure(b);
    if (x === null || y === null) {
      if (x !== y) return x === null ? 1 : -1;
      return byClose(a, b);
    }
    return y - x || byClose(a, b);
  };
}

export function sortRaises(raises: LiveRaise[], sort: SortKey): LiveRaise[] {
  const order =
    sort === 'funded'
      ? byMost((r) => fundedPct(r.raised, r.target))
      : sort === 'investors'
        ? byMost((r) => r.investors)
        : byClose;
  return [...raises].sort(order);
}

// ---- The timeline -----------------------------------------------------------------

/** A raise's dot: its area grows with its investors. */
export interface DotView {
  id: string;
  /** Its diameter in px. */
  size: number;
  /** Past its target, short of it, or not reporting what it has raised. */
  tone: 'past' | 'below' | 'none';
  /** "Myfood, 78% funded", for a screen reader. */
  label: string;
  /** The caption under the timeline while the dot or its card is active. */
  caption: string;
}

export interface ColumnView {
  /** Days from today: 0 to WINDOW_DAYS. */
  offset: number;
  /** "MON", or "TODAY". */
  weekday: string;
  /** "19". */
  day: string;
  today: boolean;
  dots: DotView[];
}

/** 12px where a raise reports no investors. */
export function dotSize(investors: number | null): number {
  return investors === null ? 12 : Math.round(10 + Math.sqrt(investors) * 1.1);
}

/**
 * "Myfood · 78% funded · 127 investors · closes Mon 19 Oct": what the
 * caption says while a raise is active. Only what the raise reports.
 */
export function captionOf(r: LiveRaise, zone: string): string {
  const pct = fundedPct(r.raised, r.target);
  const bits = [
    r.name,
    pct === null ? 'raised not reported' : `${pctText(pct)} funded`,
  ];
  if (r.investors !== null) {
    bits.push(`${count(r.investors)} investor${r.investors === 1 ? '' : 's'}`);
  }
  const close = closeOf(r);
  if (close) bits.push(`closes ${shortDay(close, zone)}`);
  return bits.join(' · ');
}

export function dotOf(r: LiveRaise, zone: string): DotView {
  const pct = fundedPct(r.raised, r.target);
  return {
    id: r.id,
    size: dotSize(r.investors),
    tone: pct === null ? 'none' : pct >= 100 ? 'past' : 'below',
    label:
      pct === null
        ? `${r.name}, raised not reported`
        : `${r.name}, ${pctText(pct)} funded`,
    caption: captionOf(r, zone),
  };
}

/**
 * Today and the ten days after it, each with the dots of the raises that
 * close on it, the most investors first (the column stacks them from the
 * bottom up).
 */
export function timelineOf(
  raises: LiveRaise[],
  now: Date,
  zone: string,
): ColumnView[] {
  const columns: ColumnView[] = Array.from(
    { length: WINDOW_DAYS + 1 },
    (_, offset) => {
      const day = dayAfter(now, offset, zone);
      const p = parts(day, zone, { weekday: 'short', day: 'numeric' });
      return {
        offset,
        weekday: offset === 0 ? 'TODAY' : p.weekday.toUpperCase(),
        day: p.day,
        today: offset === 0,
        dots: [],
      };
    },
  );
  const placed = raises
    .filter((r) => inWindow(r, now, zone))
    .sort(byMost((r) => r.investors));
  for (const r of placed) {
    columns[daysLeft(closeOf(r)!, now, zone)].dots.push(dotOf(r, zone));
  }
  return columns;
}

/** "Sun 18 – Wed 28 Oct"; each with its month where the window spans two. */
export function rangeLabel(now: Date, zone: string): string {
  const first = dayAfter(now, 0, zone);
  const last = dayAfter(now, WINDOW_DAYS, zone);
  const month = (d: Date) => parts(d, zone, { month: 'short' }).month;
  const from =
    month(first) === month(last)
      ? weekdayDay(first, zone)
      : shortDay(first, zone);
  return `${from} – ${shortDay(last, zone)}`;
}

// ---- The cards --------------------------------------------------------------------

export interface CardView {
  id: string;
  href: string;
  name: string;
  /** Its logo, or its initials on a tile coloured from its name. */
  tile: ReturnType<typeof tileOf>;
  /** "Reg CF · US". */
  chip: string | null;
  pill: { text: string; tone: string } | null;
  /** The big figure: how funded, or the target where nothing raised is reported. */
  metric: { value: string; label: string; tone: 'past' | 'ink' | 'muted' };
  investors: string;
  bar: ReturnType<typeof barView>;
  /** The line under the bar. */
  under: {
    /** Past target: the left side marks the target's sliver. */
    sliver: boolean;
    left: string;
    right: string | null;
    past: boolean;
  };
  /** "Closes Fri 23 Oct". */
  closes: string;
  /** "Opened Thu 22 Oct", on a list of what has just opened. */
  opened?: string;
}

export function cardOf(r: LiveRaise, now: Date, zone: string): CardView {
  const pct = fundedPct(r.raised, r.target);
  const close = closeOf(r);
  const target = stated(r.target) ? fullMoney(r.target, r.currency) : null;

  let metric: CardView['metric'];
  let under: CardView['under'];
  if (pct !== null && pct >= 100) {
    metric = { value: pctText(pct), label: 'funded', tone: 'past' };
    under = {
      sliver: true,
      left: `target ${target}`,
      right: `${multiple(pct)} target`,
      past: true,
    };
  } else if (pct !== null) {
    metric = { value: pctText(pct), label: 'funded', tone: 'ink' };
    under = {
      sliver: false,
      left: `${fullMoney(r.raised!, r.currency)} of ${target}`,
      right: `${fullMoney(r.target! - r.raised!, r.currency)} to go`,
      past: false,
    };
  } else if (target || r.raised === null) {
    metric = { value: target ?? '—', label: 'target', tone: 'muted' };
    under = {
      sliver: false,
      left: 'Raised not reported yet',
      right: null,
      past: false,
    };
  } else {
    // An amount raised with no target to measure it against.
    metric = {
      value: fullMoney(r.raised, r.currency),
      label: 'raised',
      tone: 'ink',
    };
    under = {
      sliver: false,
      left: 'Target not reported yet',
      right: null,
      past: false,
    };
  }

  return {
    id: r.id,
    href: `/raises/${encodeURIComponent(r.slug)}`,
    name: r.name,
    tile: tileOf(r),
    chip: regimeLabel(r.regime, r.country),
    pill: close ? urgencyOf(daysLeft(close, now, zone)) : null,
    metric,
    investors: r.investors === null ? '—' : count(r.investors),
    bar: barView(barOf(r.raised, r.target)),
    under,
    closes: close
      ? `Closes ${shortDay(close, zone)}`
      : 'Close date not published',
  };
}

// ---- The page ---------------------------------------------------------------------

/**
 * A filtered list: every live raise it holds, in place of the window (a
 * hub's, or a view /raises filters by several).
 */
export interface ListScope {
  /** "All Crowdcube raises". */
  heading: string;
  /** "No live raises on Crowdcube right now." */
  emptyText: string;
  /**
   * The raises are already in the list's own order, and stay in it: there is
   * nothing to sort, and the page offers no sort (site/week-lists.ts).
   */
  ordered?: boolean;
}

export interface IndexChoice {
  now: Date;
  zone: string;
  sort: SortKey;
  scope: ListScope | null;
}

/** What the list and the timeline show, for the reader's choices. */
export interface IndexState {
  /** The list's raises, in order. */
  shown: LiveRaise[];
  columns: ColumnView[];
  /** "12 raises · 10 days". */
  count: string;
  /** "Sun 18 – Wed 28 Oct". */
  range: string;
}

export function indexState(
  raises: LiveRaise[],
  choice: IndexChoice,
): IndexState {
  const { now, zone, scope } = choice;
  const set = scope
    ? raises.filter((r) => !closeOf(r) || closeOf(r)! > now)
    : raises.filter((r) => inWindow(r, now, zone));
  const shown = scope?.ordered ? set : sortRaises(set, choice.sort);
  const n = shown.length;
  return {
    shown,
    columns: timelineOf(shown, now, zone),
    count: `${num(n)} raise${n === 1 ? '' : 's'}${scope ? '' : ` · ${WINDOW_DAYS} days`}`,
    range: rangeLabel(now, zone),
  };
}

// ---- The way into the app ------------------------------------------------------

/**
 * The hero's button and what goes with it. Signed in it opens the app;
 * signed out it offers an account, and says which plan sends alerts.
 */
export function heroActions(appUrl: string, signedIn: boolean) {
  return {
    cta: signedIn
      ? { href: appUrl, label: `Open ${BRAND_NAME}` }
      : { href: '/signup?from=raises', label: 'Create a free account' },
    login: signedIn ? null : { href: '/login', label: 'Log in' },
    note: signedIn ? null : 'Free to start. Alerts come with the Alerts plan.',
  };
}

/** What the app adds to this page, in the About block's words. */
export const APP_FEATURES = [
  {
    icon: 'search',
    title: 'Every live raise',
    line: 'Filter by sector, country, minimum and platform.',
  },
  {
    icon: 'bell',
    title: 'Launch alerts',
    line: 'An email when a raise matching your filters opens.',
  },
  {
    icon: 'clock',
    title: 'Close reminders',
    line: 'Follow a raise and hear from us before it closes.',
  },
] as const;

/** The filters the search row shows, as the app's chips. */
export const EXAMPLE_FILTERS = ['Food & drink', 'UK', 'Min £10'];

/**
 * The live raises the panel shows as its alerts' examples, picked by the
 * controller from the whole live set: the one that launched last, and the
 * one closing next. A row whose raise is missing draws no example.
 */
export interface PanelExamples {
  launched: LiveRaise | null;
  closing: LiveRaise | null;
}

/** A launch alert for `r`: "NEW RAISE · CROWDCUBE", its name, its target. */
export function launchExample(r: LiveRaise) {
  return {
    kicker: `New raise · ${r.platformName}`,
    headline: `${r.name} is now live`,
    target: stated(r.target)
      ? `Target ${fullMoney(r.target, r.currency)}`
      : null,
  };
}

/**
 * A close reminder for `r`: its name, how soon it closes (in `zone`, which
 * the script redraws in the reader's), its bar and what it reports.
 */
export function reminderExample(r: LiveRaise, now: Date, zone: string) {
  const pct = fundedPct(r.raised, r.target);
  const figures = [
    pct === null ? 'Raised not reported' : `${pctText(pct)} funded`,
  ];
  if (r.investors !== null) {
    figures.push(
      `${count(r.investors)} investor${r.investors === 1 ? '' : 's'}`,
    );
  }
  const close = closeOf(r);
  return {
    name: r.name,
    closeAt: r.closeAt,
    urgency: close ? urgencyOf(daysLeft(close, now, zone)) : null,
    bar: barView(barOf(r.raised, r.target)),
    figures: figures.join(' · '),
  };
}

/**
 * "The Raise Sonar app" beside the hero: what the app adds to this page, a
 * row for each, the first open. The script opens each in turn.
 */
export function appPanelView(examples: PanelExamples, now: Date, zone: string) {
  const { launched, closing } = examples;
  return {
    features: APP_FEATURES.map((f, i) => ({
      ...f,
      paths: PROMO_ICON_PATHS[f.icon],
      on: i === 0,
      filters: f.icon === 'search' ? EXAMPLE_FILTERS : null,
      launch: f.icon === 'bell' && launched ? launchExample(launched) : null,
      reminder:
        f.icon === 'clock' && closing
          ? reminderExample(closing, now, zone)
          : null,
    })),
  };
}

/** A field the sort form sends again: a filter /raises took as a query. */
export interface FormField {
  name: string;
  value: string;
}

export interface RaisesIndexInput extends IndexChoice {
  /** The live raises the page could show: READ_DAYS ahead, or the scope's. */
  raises: LiveRaise[];
  /**
   * A hub's h1 and its line of counts; null for the index's own. `h1End` is
   * the end of the h1, to be kept in one piece: a date.
   */
  lede: { h1: string; intro: string; h1End?: string } | null;
  /** The address the page answers at, without its query: where the sort goes. */
  path: string;
  /** What the sort sends with it: the filters /raises took as a query. */
  fields: FormField[];
  /** The list's way back to every live raise ("All platforms"); null on /raises. */
  back: { href: string; text: string } | null;
  /** The Raise Sonar app, ending in a slash (res.locals.appUrl). */
  appUrl: string;
  /** The reader is signed in: the hero opens the app, not a sign-up. */
  signedIn: boolean;
  /** The site's facts (SiteFactsService), as every page states them. */
  stats: {
    liveCount: number | null;
    platformCount: number | null;
    countryCount: number | null;
    newPerDay: number | null;
  };
  /** The app panel's examples. */
  examples: PanelExamples;
  /** What a list says on a card beyond every card's words: a share of its maximum, the day it opened. */
  card?: (raise: LiveRaise, card: CardView) => CardView;
}

/**
 * A card's "View on the platform ↗": the raise's own page on the platform,
 * marked as sent from here, where the money goes. A Form C row has no page
 * there that we know of, so it links its filing and says so.
 */
export function outLink(r: Pick<LiveRaise, 'url' | 'filing' | 'affiliate'>) {
  return {
    href: outboundUrl(r.url),
    label: r.filing ? 'View on SEC EDGAR' : 'View on the platform',
    rel: outboundRel(!r.filing && r.affiliate),
  };
}

/** Everything views/raises.hbs draws. */
export function raisesIndexView(input: RaisesIndexInput) {
  const { raises, appUrl, signedIn, now, zone, sort, scope, stats } = input;
  const state = indexState(raises, input);
  const shownIds = new Set(state.shown.map((r) => r.id));
  const h1 = input.lede?.h1 ?? 'Live equity crowdfunding raises';
  const h1End = input.lede?.h1End ?? null;
  // Shown in order first; the rest after, hidden, for the script to bring
  // in where the reader's zone puts them.
  const ordered = [
    ...state.shown,
    ...sortRaises(
      raises.filter((r) => !shownIds.has(r.id)),
      sort,
    ),
  ];
  return {
    hero: {
      // The live count links to the app's feed, which lists them all.
      kicker:
        stats.liveCount === null
          ? null
          : { text: `${num(stats.liveCount)} live raises now`, href: appUrl },
      h1,
      h1Start: h1End ? h1.slice(0, -h1End.length).trimEnd() : null,
      h1End,
      intro: input.lede?.intro ?? promoBody(undefined),
      ...heroActions(appUrl, signedIn),
      stats: promoStats({ ...stats, appUrl }),
      panel: appPanelView(input.examples, now, zone),
    },
    timeline: {
      range: state.range,
      columns: state.columns,
    },
    list: {
      heading: scope?.heading ?? 'Closing soon',
      count: state.count,
      back: input.back,
      // A GET form, so the sort works before the script runs.
      form: { action: input.path, fields: input.fields },
      sorts: scope?.ordered
        ? []
        : SORTS.map((s) => ({ ...s, pressed: s.key === sort })),
      cards: ordered.map((r) => {
        const card = cardOf(r, now, zone);
        return {
          ...(input.card?.(r, card) ?? card),
          out: outLink(r),
          hidden: !shownIds.has(r.id),
        };
      }),
      empty: state.shown.length === 0,
      emptyText:
        scope?.emptyText ?? `No raises close in the next ${WINDOW_DAYS} days.`,
    },
    // Read by the script; "<" escaped, so no name can close the element.
    data: JSON.stringify({ raises, scope }).replace(/</g, '\\u003c'),
  };
}
